export interface IWeightedEntry {
  value: number | null
  weight: number | null
}

/** `Σ(value × weight) / Σ(weight)`, skipping entries missing either. Falls back to the plain mean
 *  when every weight is zero — the same degenerate case `getWeightedSob` handles, and for the same
 *  reason: a rate is still worth reporting when the revenue behind it happens to be nil.
 *
 *  A mean rather than a sum, deliberately. A Geo node opens into both a product subtree and its
 *  partner leaves — two overlapping partitions of the same rows — so any descendant total would
 *  double-count. A weighted mean is scale-invariant and so reads the same either way. */
export const weightedAverage = (entries: IWeightedEntry[]): number | null => {
  const present = entries.filter(
    (entry): entry is { value: number; weight: number } =>
      entry.value !== null && entry.weight !== null && Number.isFinite(entry.weight)
  )

  if (!present.length) {
    return null
  }

  const totalWeight = present.reduce((sum, { weight }) => sum + weight, 0)

  if (!totalWeight) {
    return present.reduce((sum, { value }) => sum + value, 0) / present.length
  }

  return present.reduce((sum, { value, weight }) => sum + value * weight, 0) / totalWeight
}
